/**
 * Formulaire d'une évaluation — validation PURE (testée). Les champs texte
 * arrivent tels que saisis (« 13,5 », « 2 ») et sont convertis ici.
 */

import { localDateKey, parseGradeNumber } from './grades';

export interface ScoreDraft {
  /** Note saisie (vide = pas encore notée). */
  score: string;
  /** Barème : « 20 », « 10 »… ou une valeur libre. */
  maxScore: string;
  coefficient: string;
}

export interface EvaluationDraft extends ScoreDraft {
  title: string;
  subject: string;
  evalDate: string;
  note: string;
}

export type ParsedScore =
  | { ok: true; score: number | null; max_score: number; coefficient: number }
  | { ok: false; error: string };

export function parseScoreDraft(d: ScoreDraft, requireScore = false): ParsedScore {
  const max = parseGradeNumber(d.maxScore);
  if (max == null || max <= 0 || max > 1000) {
    return { ok: false, error: 'Indique le barème (ex. 20).' };
  }
  const coef = d.coefficient.trim() === '' ? 1 : parseGradeNumber(d.coefficient);
  if (coef == null || coef <= 0 || coef > 100) {
    return { ok: false, error: 'Le coefficient doit être un nombre positif (ex. 1, 2, 0,5).' };
  }
  if (d.score.trim() === '') {
    if (requireScore) return { ok: false, error: 'Indique ta note.' };
    return { ok: true, score: null, max_score: max, coefficient: coef };
  }
  const score = parseGradeNumber(d.score);
  if (score == null) return { ok: false, error: 'La note doit être un nombre (ex. 13,5).' };
  if (score > max) return { ok: false, error: 'La note ne peut pas dépasser le barème.' };
  return { ok: true, score, max_score: max, coefficient: coef };
}

export type ParsedEvaluation =
  | {
      ok: true;
      title: string;
      subject: string | null;
      eval_date: string;
      note: string | null;
      score: number | null;
      max_score: number;
      coefficient: number;
    }
  | { ok: false; error: string };

export function parseEvaluationDraft(
  d: EvaluationDraft,
  today: string = localDateKey(),
): ParsedEvaluation {
  const title = d.title.trim();
  if (!title) return { ok: false, error: 'Donne un nom à ton éval (ex. Contrôle Pythagore).' };
  if (title.length > 120) return { ok: false, error: 'Le nom est trop long (120 caractères).' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.evalDate)) {
    return { ok: false, error: 'Choisis la date de l’éval.' };
  }
  const subject = d.subject.trim();
  if (subject.length > 60) return { ok: false, error: 'Le nom de la matière est trop long.' };
  const note = d.note.trim();
  if (note.length > 500)
    return { ok: false, error: 'La remarque est trop longue (500 caractères).' };
  // Une note ne se saisit qu'une fois l'éval passée (ou le jour même).
  const scoreDraft = d.evalDate > today ? { ...d, score: '' } : d;
  const s = parseScoreDraft(scoreDraft);
  if (!s.ok) return s;
  return {
    ok: true,
    title,
    subject: subject || null,
    eval_date: d.evalDate,
    note: note || null,
    score: s.score,
    max_score: s.max_score,
    coefficient: s.coefficient,
  };
}

/** Valeur numérique → texte de champ (« 13,5 »). */
export function numberToField(n: number | null | undefined): string {
  if (n == null) return '';
  return String(n).replace('.', ',');
}

/**
 * « Ajouter une note reçue » : l'éval a déjà eu lieu (date passée ou du jour)
 * et la note est obligatoire. Message d'erreur, sinon null. Pure.
 */
export function gradedDraftIssue(
  evalDate: string,
  score: string,
  today: string = localDateKey(),
): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(evalDate) && evalDate > today) {
    return 'Une note reçue porte sur une éval déjà passée : choisis une date passée ou aujourd’hui.';
  }
  if (score.trim() === '') return 'Indique ta note.';
  return null;
}
